import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import {
    combineLatest,
    Observable,
    of
} from 'rxjs';
import {
    first,
    map,
    switchMap
} from 'rxjs/operators';
import {
    select,
    Store
} from '@ngrx/store';
import {
    getStructureSelected,
} from '../store/selectors/authz.selectors';
import {isAuthentified } from '@pcscol-portail/ng-auth';
import { AuthzConfig, AuthzConfigParameters } from '../conf/conf';
import { AuthzService } from '../services/authz.service';
import { StructureModel } from '../store/models/structure-authz.model';

@Injectable({
    providedIn: 'root'
})
export class TraitementsGuard  {
    private router = inject(Router);
    private store = inject<Store<any>>(Store);
    private authzService = inject(AuthzService);
    private configurationFactory = inject(AuthzConfig) ;


    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot): Observable<boolean | UrlTree> {
        return combineLatest([
            this.store.pipe(select(isAuthentified)),
            this.store.pipe(select(getStructureSelected)),
        ]).pipe(
            first(),
            switchMap(([isAuth, structureSelected]) =>
              this.hasAccessToModule().pipe(map(access => [isAuth, structureSelected, access] as [boolean, StructureModel | null, boolean]))
            ),
            switchMap(([isAuth, structureSelected, isModuleAccessAuthorized]: [boolean, StructureModel | null, boolean]) => {
              if (this.configurationFactory.enableModuleAccessCheck && !isModuleAccessAuthorized) {
                // redirection vers la page permettant de sélectionner un autre module (en général le module AGE)
                return this.navigateTo('missing_module_access_uri', state);
              }
              if (!structureSelected) {
                // redirection vers la page de sélection de l'établissement (en général le module AGE)
                return this.navigateTo('select_etab_uri', state);
              }
              if (!isAuth) {
                return of(false);
              }
              if (!state.url.startsWith(`/${structureSelected.code}`)) {
                // redirection vers /{codeStructure} pour avoir le codeStructure dans l'url
                return this.navigateToUrl(`/${structureSelected.code}`, state);
              }
              return this.hasAnyTraitement(route.data['traitements']);
            }),
        );
    }

    private hasAccessToModule(): Observable<boolean> {
      if (this.configurationFactory.code_module == null) {
        return of(true);
      } else {
        return this.authzService.hasAccessToAnyModule(this.configurationFactory.code_module);
      }
    }

    private hasAnyTraitement(traitements?: string[]|null): Observable<boolean> {
      return traitements != null ? this.authzService.hasAnyTraitement(...traitements) : of(true);
    }

    private navigateTo(configKey: string, state: RouterStateSnapshot): Observable<UrlTree> {
      const url : string = (this.configurationFactory as any)[configKey];
      if (state.url === url) {
        throw new Error(`TraitementGuard: infinite navigation loop detected to uri '${url}' (invalid configuation key: ${configKey})`);
      }
      return of(this.router.parseUrl(url));
  }

  private navigateToUrl(url: string, state: RouterStateSnapshot): Observable<UrlTree> {
    if (state.url === url) {
      throw new Error(`TraitementGuard: infinite navigation loop detected to uri '${url}'`);
    }
    return of(this.router.parseUrl(url));
}
}
